// /**
//  * UserPermissionOverrideForm.jsx
//  * Route: /all-users/:userId/permissions
//  *
//  * Shows the user's ROLE permissions as baseline (grey, read-only).
//  * Admin can override specific actions per module/submodule.
//  * Changed rows are highlighted in amber.
//  * Only the DIFF (changed from role) is saved as permissionOverrides.
//  */
// import { useState, useEffect, useCallback } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import {
//   Save, ArrowLeft, Shield, User, Info,
//   ChevronDown, ChevronRight, RotateCcw,
// } from "lucide-react";
// import { toast } from "react-toastify";
// import api from "../../common/apiClient";
// import SummaryApi from "../../common/SummaryApi";
// import PageLoader from "../../helpers/PageLoader";
// import {
//   PERMISSION_MODULES,
//   ACTIONS,
//   buildInitialPermissions,
//   emptyPerms,
// } from "../../config/permissionModules";

// // ── helpers ───────────────────────────────────────────────────────────────────
// const ACTION_LABELS = { view: "View", add: "Add", edit: "Edit", delete: "Delete", export: "Export" };

// /** Deep-compare two permission rows — returns true if they differ */
// const rowDiffers = (rolePerms = {}, overridePerms = {}) =>
//   ACTIONS.some((a) => overridePerms[a] !== undefined && overridePerms[a] !== (rolePerms[a] ?? false));

// /** Build a "working" permissions map that layers overrides on top of role */
// const mergePermissions = (rolePerms = {}, overrides = {}) => {
//   const merged = { ...buildInitialPermissions() };
//   // Apply role
//   Object.keys(rolePerms).forEach((k) => {
//     if (merged[k]) {
//       const r = rolePerms[k] || {};
//       merged[k] = {
//         view: !!r.view, add: !!r.add, edit: !!r.edit,
//         delete: !!r.delete, export: !!r.export, hide: !!r.hide,
//       };
//     }
//   });
//   // Apply overrides on top
//   Object.keys(overrides || {}).forEach((k) => {
//     if (merged[k]) {
//       const o = overrides[k] || {};
//       ACTIONS.forEach((a) => {
//         if (o[a] !== undefined && o[a] !== null) merged[k][a] = !!o[a];
//       });
//     }
//   });
//   return merged;
// };

// // ─────────────────────────────────────────────────────────────────────────────
// export default function UserPermissionOverrideForm() {
//   const navigate        = useNavigate();
//   const { userId }      = useParams();

//   const [user,        setUser]        = useState(null);
//   const [rolePerms,   setRolePerms]   = useState({});   // baseline from role
//   const [working,     setWorking]     = useState({});   // merged (role + overrides) — what admin edits
//   const [savedOverrides, setSavedOverrides] = useState({});  // what's already saved
//   const [expanded,    setExpanded]    = useState(() =>
//     Object.fromEntries(PERMISSION_MODULES.slice(0, 4).map((m) => [m.key, true]))
//   );
//   const [loading,  setLoading]  = useState(true);
//   const [saving,   setSaving]   = useState(false);

//   /* ── fetch user + role permissions ───────────────────────────────────── */
//   const loadData = useCallback(async () => {
//     setLoading(true);
//     try {
//       const { data } = await api.get(SummaryApi.viewOneUser.url(userId));
//       const u = data.data;
//       setUser(u);

//       // Fetch the role's permissions
//       let rolePerm = {};
//       if (u.customRoleId?._id || u.customRoleId) {
//         const roleId = u.customRoleId?._id || u.customRoleId;
//         const roleRes = await api.get(SummaryApi.getRoleById.url(roleId));
//         rolePerm = roleRes.data.data?.permissions || {};
//       }
//       setRolePerms(rolePerm);

//       const existingOverrides = u.permissionOverrides || {};
//       setSavedOverrides(existingOverrides);
//       setWorking(mergePermissions(rolePerm, existingOverrides));
//     } catch {
//       toast.error("Failed to load user permissions");
//     } finally {
//       setLoading(false);
//     }
//   }, [userId]);

//   useEffect(() => { loadData(); }, [loadData]);

//   /* ── toggle a single action ──────────────────────────────────────────── */
//   const toggleAction = (key, action, value) => {
//     setWorking((prev) => ({
//       ...prev,
//       [key]: { ...(prev[key] || emptyPerms()), [action]: value },
//     }));
//   };

//   /** Reset one row back to role default */
//   const resetRow = (key) => {
//     const roleRow = rolePerms[key];
//     setWorking((prev) => ({
//       ...prev,
//       [key]: roleRow
//         ? { view: !!roleRow.view, add: !!roleRow.add, edit: !!roleRow.edit, delete: !!roleRow.delete, export: !!roleRow.export, hide: !!roleRow.hide }
//         : emptyPerms(),
//     }));
//   };

//   /** Reset ALL overrides back to role */
//   const resetAll = () => setWorking(mergePermissions(rolePerms, {}));

//   /* ── compute which rows have overrides (differ from role) ─────────────── */
//   const getChangedKeys = () => {
//     const changed = [];
//     Object.keys(working).forEach((key) => {
//       const rp = rolePerms[key] || emptyPerms();
//       const wp = working[key]   || emptyPerms();
//       if (ACTIONS.some((a) => wp[a] !== (rp[a] ?? false))) changed.push(key);
//     });
//     return changed;
//   };

//   const changedKeys = getChangedKeys();

//   /* ── save ─────────────────────────────────────────────────────────────── */
//   const handleSave = async () => {
//     // Build permissionOverrides = only the keys/actions that DIFFER from role
//     const overrides = {};
//     changedKeys.forEach((key) => {
//       const rp = rolePerms[key] || emptyPerms();
//       const wp = working[key]   || emptyPerms();
//       const diff = {};
//       ACTIONS.forEach((a) => {
//         if (wp[a] !== (rp[a] ?? false)) diff[a] = wp[a];
//       });
//       if (Object.keys(diff).length > 0) overrides[key] = diff;
//     });

//     setSaving(true);
//     try {
//       await api.put(SummaryApi.updateUserPermissions.url(userId), { permissionOverrides: overrides });
//       setSavedOverrides(overrides);
//       toast.success("User permissions updated");
//     } catch (err) {
//       toast.error(err.response?.data?.message || "Failed to save");
//     } finally {
//       setSaving(false);
//     }
//   };

//   const toggleExpand = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

//   if (loading) return <PageLoader />;
//   if (!user)   return null;

//   const isOwner = user.role === "owner";

//   /* ── render ────────────────────────────────────────────────────────────── */
//   return (
//     <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-6">

//       {/* Header */}
//       <div className="flex items-center gap-3 mb-6">
//         <button type="button" onClick={() => navigate(-1)}
//           className="p-2 rounded-full hover:bg-red-50 text-gray-500 hover:text-red-600 transition-colors">
//           <ArrowLeft className="w-5 h-5" />
//         </button>
//         <div className="flex items-center gap-2">
//           <div className="p-1.5 bg-red-100 rounded-lg">
//             <Shield className="w-5 h-5 text-red-600" />
//           </div>
//           <div>
//             <h1 className="text-lg sm:text-xl font-bold text-gray-800">User Permission Override</h1>
//             <p className="text-xs text-gray-400">Specific exceptions for this user only</p>
//           </div>
//         </div>
//       </div>

//       {/* User info banner */}
//       <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-4 mb-5 shadow-sm">
//         <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
//           {user.profilePicture
//             ? <img src={user.profilePicture} alt="" className="w-10 h-10 rounded-full object-cover" />
//             : <User className="w-5 h-5 text-red-600" />}
//         </div>
//         <div className="min-w-0">
//           <p className="font-semibold text-gray-800 truncate">
//             {user.firstName} {user.lastName}
//             {isOwner && <span className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">Owner</span>}
//           </p>
//           <p className="text-xs text-gray-400 truncate">{user.email}</p>
//         </div>
//         <div className="ml-auto shrink-0 text-right hidden sm:block">
//           <p className="text-xs text-gray-500">Role</p>
//           <p className="text-sm font-semibold text-gray-700">
//             {user.customRoleId?.roleName || "No Role"}
//           </p>
//         </div>
//       </div>

//       {/* Owner block */}
//       {isOwner && (
//         <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
//           <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
//           <div>
//             <p className="text-sm font-semibold text-amber-800">Owner — Full Access Always</p>
//             <p className="text-xs text-amber-700 mt-0.5">
//               The organization owner automatically has full access to everything.
//               Permission overrides do not apply to this account.
//             </p>
//           </div>
//         </div>
//       )}

//       {!isOwner && (
//         <>
//           {/* Info card */}
//           <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
//             <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
//             <div className="text-xs text-blue-700 leading-relaxed">
//               <strong>How overrides work:</strong> This user's role
//               <span className="font-semibold"> ({user.customRoleId?.roleName || "No Role"})</span> sets
//               the baseline. Use the checkboxes below to grant or restrict specific permissions
//               for this user only. <span className="font-semibold text-amber-700">Amber rows</span> are
//               overridden; white rows use the role default.
//             </div>
//           </div>

//           {/* Changed count + reset */}
//           <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
//             <div className="flex items-center gap-2">
//               {changedKeys.length > 0
//                 ? <span className="text-xs bg-amber-100 text-amber-700 font-semibold px-2.5 py-1 rounded-full">
//                     {changedKeys.length} override{changedKeys.length > 1 ? "s" : ""} active
//                   </span>
//                 : <span className="text-xs text-gray-400">No overrides — using role defaults</span>
//               }
//             </div>
//             {changedKeys.length > 0 && (
//               <button type="button" onClick={resetAll}
//                 className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-600 border border-gray-200 hover:border-red-300 px-3 py-1.5 rounded-lg transition-colors">
//                 <RotateCcw className="w-3.5 h-3.5" />
//                 Reset all to role defaults
//               </button>
//             )}
//           </div>

//           {/* Permission matrix */}
//           <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-5">

//             {/* Column headers */}
//             <div className="overflow-x-auto">
//               <div className="min-w-[620px]">
//                 <div className="grid grid-cols-[1fr_repeat(5,54px)_46px] gap-x-1 px-4 sm:px-5 py-2.5 bg-gray-50 border-b border-gray-200">
//                   <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Module / Submodule</span>
//                   {ACTIONS.map((a) => (
//                     <span key={a} className="text-[10px] font-bold text-gray-500 uppercase tracking-wider text-center">
//                       {ACTION_LABELS[a]}
//                     </span>
//                   ))}
//                   <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider text-center">Reset</span>
//                 </div>

//                 {/* Rows */}
//                 <div className="divide-y divide-gray-100">
//                   {PERMISSION_MODULES.map((mod) => {
//                     const isOpen    = expanded[mod.key];
//                     const modWork   = working[mod.key]   || emptyPerms();
//                     const modRole   = rolePerms[mod.key] || emptyPerms();
//                     const modChanged = changedKeys.includes(mod.key);

//                     return (
//                       <div key={mod.key}>
//                         {/* Module row */}
//                         <div className={`grid grid-cols-[1fr_repeat(5,54px)_46px] gap-x-1 items-center px-4 sm:px-5 py-3 transition-colors
//                           ${modChanged ? "bg-amber-50 border-l-2 border-l-amber-400" : "hover:bg-gray-50"}`}>
//                           <div className="flex items-center gap-2 min-w-0">
//                             <button type="button" onClick={() => toggleExpand(mod.key)}
//                               className="shrink-0 text-gray-400 hover:text-gray-600 transition-colors">
//                               {(mod.submodules?.length || 0) > 0
//                                 ? isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
//                                 : <span className="w-4 h-4 block" />}
//                             </button>
//                             <span className="font-semibold text-sm text-gray-800 truncate">{mod.label}</span>
//                             {modChanged && (
//                               <span className="shrink-0 text-[10px] bg-amber-200 text-amber-700 font-bold px-1.5 py-0.5 rounded">
//                                 OVERRIDE
//                               </span>
//                             )}
//                           </div>

//                           {ACTIONS.map((action) => {
//                             const roleVal    = !!(modRole[action]);
//                             const workingVal = !!(modWork[action]);
//                             const differs    = workingVal !== roleVal;
//                             return (
//                               <div key={action} className="flex flex-col items-center gap-0.5">
//                                 <input
//                                   type="checkbox"
//                                   checked={workingVal}
//                                   onChange={(e) => toggleAction(mod.key, action, e.target.checked)}
//                                   className={`w-4 h-4 cursor-pointer ${differs ? "accent-amber-500" : "accent-red-600"}`}
//                                 />
//                                 {/* Role baseline indicator */}
//                                 <span className={`text-[9px] leading-none ${roleVal ? "text-green-500" : "text-gray-300"}`}>
//                                   {roleVal ? "✓" : "—"}
//                                 </span>
//                               </div>
//                             );
//                           })}

//                           <div className="flex justify-center">
//                             {modChanged && (
//                               <button type="button" onClick={() => resetRow(mod.key)}
//                                 title="Reset to role default"
//                                 className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
//                                 <RotateCcw className="w-3.5 h-3.5" />
//                               </button>
//                             )}
//                           </div>
//                         </div>

//                         {/* Submodule rows */}
//                         {isOpen && (mod.submodules || []).map((sub) => {
//                           const subWork    = working[sub.key]   || emptyPerms();
//                           const subRole    = rolePerms[sub.key] || emptyPerms();
//                           const subChanged = changedKeys.includes(sub.key);

//                           return (
//                             <div key={sub.key}
//                               className={`grid grid-cols-[1fr_repeat(5,54px)_46px] gap-x-1 items-center pl-10 sm:pl-14 pr-4 sm:pr-5 py-2.5 border-t border-gray-100 transition-colors
//                                 ${subChanged ? "bg-amber-50 border-l-2 border-l-amber-400" : "bg-slate-50/60 hover:bg-gray-50"}`}>
//                               <div className="flex items-center gap-2 min-w-0">
//                                 <div className="w-3 h-px bg-gray-300 shrink-0" />
//                                 <span className="text-sm text-gray-700 truncate">{sub.label}</span>
//                                 {subChanged && (
//                                   <span className="shrink-0 text-[10px] bg-amber-200 text-amber-700 font-bold px-1.5 py-0.5 rounded">
//                                     OVERRIDE
//                                   </span>
//                                 )}
//                               </div>

//                               {ACTIONS.map((action) => {
//                                 const roleVal    = !!(subRole[action]);
//                                 const workingVal = !!(subWork[action]);
//                                 const differs    = workingVal !== roleVal;
//                                 return (
//                                   <div key={action} className="flex flex-col items-center gap-0.5">
//                                     <input
//                                       type="checkbox"
//                                       checked={workingVal}
//                                       onChange={(e) => toggleAction(sub.key, action, e.target.checked)}
//                                       className={`w-4 h-4 cursor-pointer ${differs ? "accent-amber-500" : "accent-red-600"}`}
//                                     />
//                                     <span className={`text-[9px] leading-none ${roleVal ? "text-green-500" : "text-gray-300"}`}>
//                                       {roleVal ? "✓" : "—"}
//                                     </span>
//                                   </div>
//                                 );
//                               })}

//                               <div className="flex justify-center">
//                                 {subChanged && (
//                                   <button type="button" onClick={() => resetRow(sub.key)}
//                                     title="Reset to role default"
//                                     className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
//                                     <RotateCcw className="w-3.5 h-3.5" />
//                                   </button>
//                                 )}
//                               </div>
//                             </div>
//                           );
//                         })}
//                       </div>
//                     );
//                   })}
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Legend */}
//           <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 mb-6 px-1">
//             <div className="flex items-center gap-1.5">
//               <span className="text-green-500 font-bold">✓</span>
//               <span>Role grants this</span>
//             </div>
//             <div className="flex items-center gap-1.5">
//               <span className="text-gray-300 font-bold">—</span>
//               <span>Role denies this</span>
//             </div>
//             <div className="flex items-center gap-1.5">
//               <div className="w-4 h-4 rounded border-2 border-amber-400 bg-amber-50" />
//               <span>Overridden (differs from role)</span>
//             </div>
//           </div>

//           {/* Footer */}
//           <div className="flex gap-3 justify-end pb-10">
//             <button type="button" onClick={() => navigate(-1)}
//               disabled={saving}
//               className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50">
//               Cancel
//             </button>
//             <button type="button" onClick={handleSave}
//               disabled={saving || changedKeys.length === 0}
//               className="inline-flex items-center gap-2 px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 shadow-sm">
//               <Save className="w-4 h-4" />
//               {saving ? "Saving…" : `Save ${changedKeys.length > 0 ? `(${changedKeys.length})` : ""} Overrides`}
//             </button>
//           </div>
//         </>
//       )}
//     </div>
//   );
// }

/**
 * UserPermissionOverrideForm.jsx
 */
// import { useState, useEffect, useCallback, useMemo } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import {
//   Save, ArrowLeft, Shield, User,
//   ChevronDown, ChevronRight, RotateCcw,  
// } from "lucide-react";
// import { MdOutlineCancel } from "react-icons/md";

// import { toast } from "react-toastify";
// import api from "../../common/apiClient";
// import SummaryApi from "../../common/SummaryApi";
// import PageLoader from "../../helpers/PageLoader";
// import {
//   PERMISSION_MODULES,
//   ACTIONS,
//   buildInitialPermissions,
//   emptyPerms,
// } from "../../config/permissionModules";

// // ── helpers ───────────────────────────────────────────────────────────────────
// const ACTION_LABELS = { view: "View", add: "Add", edit: "Edit", delete: "Delete", export: "Export" };

// /** Deep-compare: ஒரு வரிசையின் working perms, role perms-லிருந்து மாறுகிறதா எனப் பார்க்கும் */
// const rowDiffers = (rolePerms = {}, workingPerms = {}) =>
//   ACTIONS.some((a) => (workingPerms[a] ?? false) !== (rolePerms[a] ?? false));

// /** Baseline (Role) + Overrides 'working'  */
// const mergePermissions = (rolePerms = {}, overrides = {}) => {
//   const merged = buildInitialPermissions(); // Default false values
  
//   // 1. Apply role baseline
//   Object.keys(rolePerms).forEach((k) => {
//     if (merged[k]) {
//       merged[k] = { ...merged[k], ...rolePerms[k] };
//     }
//   });

//   // 2. Apply user overrides
//   Object.keys(overrides).forEach((k) => {
//     if (merged[k]) {
//       ACTIONS.forEach((a) => {
//         if (overrides[k][a] !== undefined) merged[k][a] = !!overrides[k][a];
//       });
//     }
//   });
//   return merged;
// };

// // ─────────────────────────────────────────────────────────────────────────────
// export default function UserPermissionOverrideForm() {
//   const navigate = useNavigate();
//   const { userId } = useParams();

//   const [user, setUser] = useState(null);
//   const [rolePerms, setRolePerms] = useState({});   // Role-ன் அடிப்படை உரிமைகள்
//   const [working, setWorking] = useState({});       // Admin எடிட் செய்யும் உரிமைகள்
//   const [savedOverrides, setSavedOverrides] = useState({}); // ஏற்கனவே DB-ல் சேமிக்கப்பட்ட overrides
  
//   const [expanded, setExpanded] = useState(() =>
//     Object.fromEntries(PERMISSION_MODULES.slice(0, 4).map((m) => [m.key, true]))
//   );
//   const [loading, setLoading] = useState(true);
//   const [saving, setSaving] = useState(false);

//   /* ── data fetch ──────────────────────────────────────────────────────── */
//   const loadData = useCallback(async () => {
//     setLoading(true);
//     try {
//       const { data } = await api.get(SummaryApi.viewOneUser.url(userId));
//       const u = data.data;
//       setUser(u);

//       let rolePerm = {};
//       const roleId = u.customRoleId?._id || u.customRoleId;
//       if (roleId) {
//         const roleRes = await api.get(SummaryApi.getRoleById.url(roleId));
//         rolePerm = roleRes.data.data?.permissions || {};
//       }
      
//       setRolePerms(rolePerm);
//       const existingOverrides = u.permissionOverrides || {};
//       setSavedOverrides(existingOverrides);
//       setWorking(mergePermissions(rolePerm, existingOverrides));
//     } catch {
//       toast.error("Failed to load user permissions");
//     } finally {
//       setLoading(false);
//     }
//   }, [userId]);

//   useEffect(() => { loadData(); }, [loadData]);

//   /* ── computed values ─────────────────────────────────────────────────── */
  
//   // தற்போது எவையெல்லாம் ரோலில் இருந்து மாற்றப்பட்டுள்ளன (Overrides)
//   const changedKeys = useMemo(() => {
//     return Object.keys(working).filter(key => 
//       rowDiffers(rolePerms[key] || emptyPerms(), working[key])
//     );
//   }, [working, rolePerms]);

//   // DB-ல் உள்ளதற்கும் இப்போது எடிட் செய்ததற்கும் மாற்றம் இருக்கிறதா? (Dirty check for Save button)
//   const hasUnsavedChanges = useMemo(() => {
//     // Current diff from role
//     const currentDiff = {};
//     changedKeys.forEach(key => {
//         const rp = rolePerms[key] || emptyPerms();
//         const wp = working[key];
//         const diff = {};
//         ACTIONS.forEach(a => {
//             if (wp[a] !== (rp[a] ?? false)) diff[a] = wp[a];
//         });
//         currentDiff[key] = diff;
//     });
//     return JSON.stringify(currentDiff) !== JSON.stringify(savedOverrides);
//   }, [changedKeys, working, rolePerms, savedOverrides]);

//   /* ── handlers ────────────────────────────────────────────────────────── */
//   const toggleAction = (key, action, value) => {
//     setWorking(prev => ({
//       ...prev,
//       [key]: { ...(prev[key] || emptyPerms()), [action]: value },
//     }));
//   };

//   const resetRow = (key) => {
//     const roleRow = rolePerms[key] || emptyPerms();
//     setWorking(prev => ({
//       ...prev,
//       [key]: { ...emptyPerms(), ...roleRow }
//     }));
//   };

//   const resetAll = () => setWorking(mergePermissions(rolePerms, {}));

//   const handleSave = async () => {
//     const overrides = {};
//     changedKeys.forEach((key) => {
//       const rp = rolePerms[key] || emptyPerms();
//       const wp = working[key];
//       const diff = {};
//       ACTIONS.forEach((a) => {
//         if (wp[a] !== (rp[a] ?? false)) diff[a] = wp[a];
//       });
//       if (Object.keys(diff).length > 0) overrides[key] = diff;
//     });

//     setSaving(true);
//     try {
//       await api.put(SummaryApi.updateUserPermissions.url(userId), { permissionOverrides: overrides });
//       setSavedOverrides(overrides); // Update saved reference
//       toast.success("User permissions updated successfully");
//     } catch (err) {
//       toast.error(err.response?.data?.message || "Failed to save");
//     } finally {
//       setSaving(false);
//     }
//   };

//   if (loading) return <PageLoader />;
//   if (!user) return null;

//   const isOwner = user.role === "owner";

//   return (
//     <div className="p-2">
//       {/* Header */}
//       <div className="flex items-center gap-3 mb-6">
//         <button type="button" onClick={() => navigate(-1)}
//           className="p-2 rounded-lg bg-red-50 border border-red-100 hover:border-primary text-textPrimary hover:text-primary transition-colors">
//           <ArrowLeft className="w-5 h-5" />
//         </button>
//         <div>
//           <h1 className="text-xl font-bold text-textPrimary">Permission Overrides</h1>
//           <p className="text-[11px] font-semibold text-gray-400">Custom access for {user.firstName} {user.lastName}</p>
//         </div>
//       </div>

//       {/* User Banner */}
//       <div className="bg-white rounded-lg p-4 mb-5 shadow-sm flex items-center gap-4 hover:bg-red-50">
//          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center border-2 border-primary overflow-hidden">
//             {user.profilePicture ? <img src={user.profilePicture} className="w-full h-full object-cover" alt=""/> : <User className="text-primary"/>}
//          </div>
//          <div className="flex-1">
//             <h2 className="font-bold text-gray-900">{user.firstName} {user.lastName}</h2>
//             <p className="text-sm text-gray-500">{user.customRoleId?.roleName || 'No Role Assigned'}</p>
//          </div>
//          {hasUnsavedChanges && (
//             <span className="text-[10px] font-bold bg-blue-100 text-blue-600 px-2 py-1 rounded-full animate-pulse">
//                 UNSAVED CHANGES
//             </span>
//          )}
//       </div>

//       {!isOwner ? (
//         <>
//           {/* Controls */}
//           <div className="flex items-center justify-between mb-4">
//             <div className="flex items-center gap-2">
//                <span className="text-xs font-medium text-gray-500">
//                  {changedKeys.length} active overrides
//                </span>
//             </div>
//             {changedKeys.length > 0 && (
//               <button onClick={resetAll} className="text-xs flex items-center gap-1 text-gray-500 hover:text-primary">
//                 <RotateCcw className="w-3 h-3"/> Reset to Role
//               </button>
//             )}
//           </div>

//           {/* Matrix */}
//           <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
//             <div className="overflow-x-auto">
//               <div className="min-w-[650px]">
//                 {/* Table Header */}
//                 <div className="grid grid-cols-[1fr_repeat(5,60px)_50px] gap-1 px-5 py-3 bg-red-100 text-[12px] font-semibold text-textMuted uppercase">
//                   <span>Module</span>
//                   {ACTIONS.map(a => <span key={a} className="text-center">{ACTION_LABELS[a]}</span>)}
//                   <span className="text-center">Reset</span>
//                 </div>

//                 {/* Modules */}
//                 <div className="divide-y">
//                   {PERMISSION_MODULES.map(mod => {
//                     const isModExpanded = expanded[mod.key];
//                     const isModChanged = changedKeys.includes(mod.key);
//                     const mRole = rolePerms[mod.key] || emptyPerms();
//                     const mWork = working[mod.key] || emptyPerms();

//                     return (
//                       <div key={mod.key}>
//                         <div className={`grid grid-cols-[1fr_repeat(5,60px)_50px] gap-1 px-5 py-3 items-center transition-colors ${isModChanged ? 'bg-amber-50/50' : 'hover:bg-red-50'}`}>
//                           <div className="flex items-center gap-2">
//                             <button onClick={() => setExpanded(p => ({...p, [mod.key]: !p[mod.key]}))}>
//                               {mod.submodules?.length > 0 ? (isModExpanded ? <ChevronDown size={14}/> : <ChevronRight size={14}/>) : <div className="w-3.5"/>}
//                             </button>
//                             <span className="text-sm font-semibold text-gray-700">{mod.label}</span>
//                             {isModChanged && <span className="text-[9px] bg-amber-200 text-amber-800 px-1 rounded font-bold">MODIFIED</span>}
//                           </div>

//                           {ACTIONS.map(action => {
//                             const rVal = !!mRole[action];
//                             const wVal = !!mWork[action];
//                             const isDiff = rVal !== wVal;
//                             return (
//                               <div key={action} className="flex flex-col items-center">
//                                 <input 
//                                   type="checkbox" 
//                                   checked={wVal} 
//                                   onChange={(e) => toggleAction(mod.key, action, e.target.checked)}
//                                   className={`w-4 h-4 rounded cursor-pointer ${isDiff ? 'accent-amber-500' : 'accent-primary'}`}
//                                 />
//                                 <span className={`text-[9px] mt-0.5 ${rVal ? 'text-green-500' : 'text-gray-300'}`}>{rVal ? '✓' : '—'}</span>
//                               </div>
//                             );
//                           })}

//                           <div className="flex justify-center">
//                             {isModChanged && <button onClick={() => resetRow(mod.key)} className="text-gray-400 hover:text-primary"><RotateCcw size={14}/></button>}
//                           </div>
//                         </div>

//                         {/* Submodules */}
//                         {isModExpanded && mod.submodules?.map(sub => {
//                           const isSubChanged = changedKeys.includes(sub.key);
//                           const sRole = rolePerms[sub.key] || emptyPerms();
//                           const sWork = working[sub.key] || emptyPerms();

//                           return (
//                             <div key={sub.key} className={`grid grid-cols-[1fr_repeat(5,60px)_50px] gap-1 pl-12 pr-5 py-2 items-center border-t border-gray-50 ${isSubChanged ? 'bg-amber-50/30' : 'bg-gray-50/30'}`}>
//                               <span className="text-xs text-gray-600">{sub.label}</span>
//                               {ACTIONS.map(action => {
//                                 const rVal = !!sRole[action];
//                                 const wVal = !!sWork[action];
//                                 const isDiff = rVal !== wVal;
//                                 return (
//                                   <div key={action} className="flex flex-col items-center">
//                                     <input 
//                                       type="checkbox" 
//                                       checked={wVal} 
//                                       onChange={(e) => toggleAction(sub.key, action, e.target.checked)}
//                                       className={`w-3.5 h-3.5 rounded cursor-pointer ${isDiff ? 'accent-amber-500' : 'accent-primary'}`}
//                                     />
//                                     <span className={`text-[8px] ${rVal ? 'text-green-500' : 'text-gray-300'}`}>{rVal ? '✓' : '—'}</span>
//                                   </div>
//                                 );
//                               })}
//                               <div className="flex justify-center">
//                                 {isSubChanged && <button onClick={() => resetRow(sub.key)} className="text-gray-400 hover:text-primary"><RotateCcw size={12}/></button>}
//                               </div>
//                             </div>
//                           );
//                         })}
//                       </div>
//                     );
//                   })}
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Action Bar */}
//           <div className="flex items-center justify-end gap-3 pb-10">
//             <button onClick={() => navigate(-1)} className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-gray-800 bg-gray-300 rounded-lg transition-colors">
//               <MdOutlineCancel size={16}/>Cancel
//             </button>
//             <button 
//               onClick={handleSave} 
//               disabled={saving || !hasUnsavedChanges}
//               className="flex items-center gap-2 bg-primary hover:bg-primary disabled:bg-gray-300 text-white px-8 py-2 rounded-lg text-sm font-bold shadow-md transition-all active:scale-95"
//             >
//               <Save size={16}/>
//               {saving ? "Saving..." : "Save"}
//             </button>
//           </div>
//         </>
//       ) : (
//         <div className="bg-amber-50 border border-amber-200 p-6 rounded-xl text-center">
//            <Shield className="mx-auto text-amber-500 mb-2" size={32}/>
//            <h3 className="font-bold text-amber-800">Owner Account</h3>
//            <p className="text-sm text-amber-700">Owner profiles have absolute access. Overrides are not applicable.</p>
//         </div>
//       )}
//     </div>
//   );
// }

/**
 * UserPermissionOverridePage.jsx
 * Route: /all-users/:userId/permissions
 *
 * Role permissions = baseline (shown as small ✓/— under each checkbox).
 * Admin edits checkboxes → amber rows = overridden from role.
 * Only the DIFF from role is saved as user.permissionOverrides.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Save, ArrowLeft, Shield, User, Info,
  ChevronDown, ChevronRight, RotateCcw,
  Building,
} from "lucide-react";
import { toast } from "react-toastify";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import PageLoader from "../../helpers/PageLoader";
import {
  PERMISSION_MODULES,
  ACTIONS,
  buildInitialPermissions,
  emptyPerms,
} from "../../config/permissionModules";

const ACTION_LABELS = { view: "View", add: "Add", edit: "Edit", delete: "Delete", export: "Export" };

/** Merge role perms + existing overrides into a working copy */
const mergePermissions = (rolePerms = {}, overrides = {}) => {
  const merged = buildInitialPermissions();
  // layer 1: role
  Object.keys(rolePerms).forEach((k) => {
    if (merged[k]) {
      const r = rolePerms[k] || {};
      merged[k] = {
        view: !!r.view, add: !!r.add, edit: !!r.edit,
        delete: !!r.delete, export: !!r.export, hide: !!r.hide,
      };
    }
  });
  // layer 2: existing overrides on top
  Object.keys(overrides || {}).forEach((k) => {
    if (merged[k]) {
      const o = overrides[k] || {};
      ACTIONS.forEach((a) => {
        if (o[a] !== undefined && o[a] !== null) merged[k][a] = !!o[a];
      });
    }
  });
  return merged;
};

export default function UserPermissionOverridePage() {
  const navigate   = useNavigate();
  const { userId } = useParams();

  const [user,      setUser]      = useState(null);
  const [rolePerms, setRolePerms] = useState({});  // baseline from role (read-only)
  const [working,   setWorking]   = useState({});  // what admin is editing
  const [expanded,  setExpanded]  = useState(() =>
    Object.fromEntries(PERMISSION_MODULES.slice(0, 4).map((m) => [m.key, true]))
);

  const [allDivisions, setAllDivisions] = useState([]);
  const [selectedDivisions, setSelectedDivisions] = useState([]); // user's allowed divisions
  const [savingDivisions, setSavingDivisions] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  /* ── load user + role ─────────────────────────────────────────────────── */
  // const loadData = useCallback(async () => {
  //   setLoading(true);
  //   try {
  //     const { data } = await api.get(SummaryApi.viewOneUser.url(userId));
  //     const u = data.data;
  //     setUser(u);

  //     let rp = {};
  //     const roleId = u.customRoleId?._id || u.customRoleId;
  //     if (roleId) {
  //       const roleRes = await api.get(SummaryApi.getRoleById.url(roleId));
  //       rp = roleRes.data.data?.permissions || {};
  //     }
  //     setRolePerms(rp);

  //     // merge existing overrides on top of role so checkboxes show current effective state
  //     setWorking(mergePermissions(rp, u.permissionOverrides || {}));
  //   } catch {
  //     toast.error("Failed to load user permissions");
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [userId]);
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Divisions matrum User details-ah parallel-a fetch panrom
      const [userRes, divRes] = await Promise.all([
        api.get(SummaryApi.viewOneUser.url(userId)),
        api.get(SummaryApi.getAllDivisions.url) // Unkaloda divisions get panra api
      ]);

      const u = userRes.data.data;
      setUser(u);
      setSelectedDivisions(u.allowedDivisions || []); // Already saved divisions

      if (divRes.data.success) {
        setAllDivisions(divRes.data.data || []);
      }

      let rp = {};
      const roleId = u.customRoleId?._id || u.customRoleId;
      if (roleId) {
        const roleRes = await api.get(SummaryApi.getRoleById.url(roleId));
        rp = roleRes.data.data?.permissions || {};
      }
      setRolePerms(rp);
      setWorking(mergePermissions(rp, u.permissionOverrides || {}));
    } catch {
      toast.error("Failed to load user permissions & divisions");
    } finally {
      setLoading(false);
    }
  }, [userId]);
  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveDivisions = async () => {
    setSavingDivisions(true);
    try {
      await api.put(SummaryApi.updateUserDivisions.url(userId), { 
        allowedDivisions: selectedDivisions 
      });
      toast.success("User divisions updated successfully");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update divisions");
    } finally {
      setSavingDivisions(false);
    }
  };

  /* ── mutators ─────────────────────────────────────────────────────────── */
  const toggleAction = (key, action, value) =>
    setWorking((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || emptyPerms()), [action]: value },
    }));

  const resetRow = (key) => {
    const base = rolePerms[key];
    setWorking((prev) => ({
      ...prev,
      [key]: base
        ? { view: !!base.view, add: !!base.add, edit: !!base.edit, delete: !!base.delete, export: !!base.export, hide: !!base.hide }
        : emptyPerms(),
    }));
  };

  const resetAll = () => setWorking(mergePermissions(rolePerms, {}));

  const toggleExpand = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  /* ── compute changed rows (differs from role baseline) ───────────────── */
  const changedKeys = Object.keys(working).filter((key) => {
    const rp = rolePerms[key] || emptyPerms();
    const wp = working[key]   || emptyPerms();
    return ACTIONS.some((a) => wp[a] !== (rp[a] ?? false));
  });

  /* ── save — only persist the DIFF from role ──────────────────────────── */
  const handleSave = async () => {
    const overrides = {};
    changedKeys.forEach((key) => {
      const rp   = rolePerms[key] || emptyPerms();
      const wp   = working[key]   || emptyPerms();
      const diff = {};
      ACTIONS.forEach((a) => {
        if (wp[a] !== (rp[a] ?? false)) diff[a] = wp[a];
      });
      if (Object.keys(diff).length > 0) overrides[key] = diff;
    });

    setSaving(true);
    try {
      await api.put(SummaryApi.updateUserPermissions.url(userId), { permissionOverrides: overrides });
      toast.success("User permissions updated");
      // refresh so baseline indicators update
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  /* ── render ───────────────────────────────────────────────────────────── */
  if (loading) return <PageLoader />;
  if (!user)   return null;

  const isOwner = user.role === "owner";

  return (
    <div className="p-2">

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={() => navigate(-1)}
          className="p-2 rounded-lg bg-red-50 border border-red-100 hover:border-primary text-textPrimary hover:text-primary transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="p-1.5 bg-red-100 rounded-lg">
          <Shield className="w-5 h-5 text-red-600" />
        </div>
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-gray-800">User Permission Override</h1>
          <p className="text-xs text-gray-400">Exceptions on top of role — for this user only</p>
        </div>
      </div>

      {/* User card */}
      <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-4 mb-5 shadow-sm">
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0 overflow-hidden">
          {user.profilePicture
            ? <img src={user.profilePicture} alt="" className="w-10 h-10 object-cover" />
            : <User className="w-5 h-5 text-red-600" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-800 truncate">
            {user.firstName} {user.lastName}
            {isOwner && (
              <span className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">Owner</span>
            )}
          </p>
          <p className="text-xs text-gray-400 truncate">{user.email}</p>
        </div>
        <div className="shrink-0 text-right hidden sm:block">
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Role</p>
          <p className="text-sm font-semibold text-gray-700">
            {user.customRoleId?.roleName || "No Role"}
          </p>
        </div>
      </div>


{/* ── DIVISIONS ACCESS SECTION ── */}
      {/* {!isOwner && ( */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <Building className="w-4 h-4 text-red-600" />
              Allowed Divisions Access (Data Visibility)
            </h2>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSelectedDivisions(allDivisions.map(d => d._id))}
                className="text-xs text-blue-600 hover:underline font-medium"
              >
                Select All
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={() => setSelectedDivisions([])}
                className="text-xs text-red-600 hover:underline font-medium"
              >
                Clear All
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-400 mb-4">
            Select which divisions this user is allowed to view and manage data for. If none selected, they won't see division-restricted data.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            {allDivisions.map((div) => {
              const isChecked = selectedDivisions.includes(div._id);
              return (
                <label key={div._id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${isChecked ? 'bg-red-50/50 border-red-300' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedDivisions([...selectedDivisions, div._id]);
                      } else {
                        setSelectedDivisions(selectedDivisions.filter(id => id !== div._id));
                      }
                    }}
                    className="w-4 h-4 accent-red-600 cursor-pointer"
                  />
                  <span className="text-sm font-medium text-gray-700">{div.name}</span>
                </label>
              );
            })}
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSaveDivisions}
              disabled={savingDivisions}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {savingDivisions ? "Saving Divisions..." : "Save Divisions Access"}
            </button>
          </div>
        </div>

      {/* Owner — no overrides allowed */}
      {isOwner && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Owner — Full Access Always</p>
            <p className="text-xs text-amber-700 mt-0.5">
              The organization owner always has full access. Overrides do not apply.
            </p>
          </div>
        </div>
      )}

      {!isOwner && (
        <>
          {/* How-it-works banner */}
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
            <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700 leading-relaxed">
              <strong>How overrides work:</strong> The role
              <strong> ({user.customRoleId?.roleName || "No Role"})</strong> sets the baseline.
              The small <span className="text-green-600 font-bold">✓</span> / <span className="text-gray-400">—</span> below
              each checkbox shows what the role grants. Changing a checkbox creates an
              <span className="text-amber-600 font-semibold"> amber override</span> for this user only.
            </p>
          </div>

          {/* Override count + reset */}
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            {changedKeys.length > 0 ? (
              <span className="text-xs bg-amber-100 text-amber-700 font-semibold px-2.5 py-1 rounded-full">
                {changedKeys.length} override{changedKeys.length > 1 ? "s" : ""} active
              </span>
            ) : (
              <span className="text-xs text-gray-400">No overrides — fully using role defaults</span>
            )}
            {changedKeys.length > 0 && (
              <button type="button" onClick={resetAll}
                className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-600 border border-gray-200 hover:border-red-300 px-3 py-1.5 rounded-lg transition-colors">
                <RotateCcw className="w-3.5 h-3.5" />
                Reset all to role defaults
              </button>
            )}
          </div>

          {/* Matrix */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-5">
            <div className="overflow-x-auto">
              <div className="min-w-[620px]">

                {/* Column headers */}
                <div className="grid grid-cols-[1fr_repeat(5,54px)_46px] gap-x-1 px-4 sm:px-5 py-2.5 bg-gray-50 border-b border-gray-200">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Module / Submodule</span>
                  {ACTIONS.map((a) => (
                    <span key={a} className="text-[10px] font-bold text-gray-500 uppercase tracking-wider text-center">
                      {ACTION_LABELS[a]}
                    </span>
                  ))}
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider text-center">Reset</span>
                </div>

                <div className="divide-y divide-gray-100">
                  {PERMISSION_MODULES.map((mod) => {
                    const isOpen     = expanded[mod.key];
                    const modWork    = working[mod.key]   || emptyPerms();
                    const modRole    = rolePerms[mod.key] || emptyPerms();
                    const modChanged = changedKeys.includes(mod.key);

                    return (
                      <div key={mod.key}>
                        {/* Module row */}
                        <div className={`grid grid-cols-[1fr_repeat(5,54px)_46px] gap-x-1 items-center px-4 sm:px-5 py-3 transition-colors
                          ${modChanged ? "bg-amber-50 border-l-4 border-l-amber-400" : "hover:bg-gray-50"}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <button type="button" onClick={() => toggleExpand(mod.key)}
                              className="shrink-0 text-gray-400 hover:text-gray-600 transition-colors">
                              {(mod.submodules?.length || 0) > 0
                                ? isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
                                : <span className="w-4 h-4 block" />}
                            </button>
                            <span className="font-semibold text-sm text-gray-800 truncate">{mod.label}</span>
                            {modChanged && (
                              <span className="shrink-0 text-[10px] bg-amber-200 text-amber-700 font-bold px-1.5 py-0.5 rounded">
                                OVERRIDE
                              </span>
                            )}
                          </div>

                          {ACTIONS.map((action) => {
                            const roleVal = !!(modRole[action]);
                            const workVal = !!(modWork[action]);
                            const differs = workVal !== roleVal;
                            return (
                              <div key={action} className="flex flex-col items-center gap-0.5">
                                <input type="checkbox"
                                  checked={workVal}
                                  onChange={(e) => toggleAction(mod.key, action, e.target.checked)}
                                  className={`w-4 h-4 cursor-pointer ${differs ? "accent-amber-500" : "accent-red-600"}`}
                                />
                                {/* role baseline indicator */}
                                <span className={`text-[9px] leading-none font-bold ${roleVal ? "text-green-500" : "text-gray-300"}`}>
                                  {roleVal ? "✓" : "—"}
                                </span>
                              </div>
                            );
                          })}

                          <div className="flex justify-center">
                            {modChanged && (
                              <button type="button" onClick={() => resetRow(mod.key)}
                                title="Reset to role default"
                                className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                                <RotateCcw className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Submodule rows */}
                        {isOpen && (mod.submodules || []).map((sub) => {
                          const subWork    = working[sub.key]   || emptyPerms();
                          const subRole    = rolePerms[sub.key] || emptyPerms();
                          const subChanged = changedKeys.includes(sub.key);

                          return (
                            <div key={sub.key}
                              className={`grid grid-cols-[1fr_repeat(5,54px)_46px] gap-x-1 items-center pl-10 sm:pl-14 pr-4 sm:pr-5 py-2.5 border-t border-gray-100 transition-colors
                                ${subChanged ? "bg-amber-50 border-l-4 border-l-amber-400" : "bg-slate-50/60 hover:bg-gray-50"}`}>
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-3 h-px bg-gray-300 shrink-0" />
                                <span className="text-sm text-gray-700 truncate">{sub.label}</span>
                                {subChanged && (
                                  <span className="shrink-0 text-[10px] bg-amber-200 text-amber-700 font-bold px-1.5 py-0.5 rounded">
                                    OVERRIDE
                                  </span>
                                )}
                              </div>

                              {ACTIONS.map((action) => {
                                const roleVal = !!(subRole[action]);
                                const workVal = !!(subWork[action]);
                                const differs = workVal !== roleVal;
                                return (
                                  <div key={action} className="flex flex-col items-center gap-0.5">
                                    <input type="checkbox"
                                      checked={workVal}
                                      onChange={(e) => toggleAction(sub.key, action, e.target.checked)}
                                      className={`w-4 h-4 cursor-pointer ${differs ? "accent-amber-500" : "accent-red-600"}`}
                                    />
                                    <span className={`text-[9px] leading-none font-bold ${roleVal ? "text-green-500" : "text-gray-300"}`}>
                                      {roleVal ? "✓" : "—"}
                                    </span>
                                  </div>
                                );
                              })}

                              <div className="flex justify-center">
                                {subChanged && (
                                  <button type="button" onClick={() => resetRow(sub.key)}
                                    title="Reset to role default"
                                    className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                                    <RotateCcw className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 mb-6 px-1">
            <div className="flex items-center gap-1.5">
              <span className="text-green-500 font-bold text-sm">✓</span>
              <span>Role grants this</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-gray-300 font-bold text-sm">—</span>
              <span>Role denies this</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-3 rounded border-2 border-amber-400 bg-amber-50" />
              <span>Overridden from role</span>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 justify-end pb-10">
            <button type="button" onClick={() => navigate(-1)} disabled={saving}
              className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button type="button" onClick={handleSave}
              disabled={saving || changedKeys.length === 0}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 shadow-sm">
              <Save className="w-4 h-4" />
              {saving ? "Saving…" : `Save Overrides${changedKeys.length > 0 ? ` (${changedKeys.length})` : ""}`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}